import { ref, onMounted, onUnmounted } from 'vue'

interface UseTurnstileOptions {
  onSuccess?: (token: string) => void
  onError?: (error: Error) => void
  onExpired?: () => void
  theme?: 'light' | 'dark' | 'auto'
}

interface UseTurnstileReturn {
  token: ReturnType<typeof ref<string | null>>
  isLoading: ReturnType<typeof ref<boolean>>
  error: ReturnType<typeof ref<string | null>>
  isBypass: boolean
  renderWidget: (element: HTMLElement) => Promise<string | null>
  resetWidget: () => void
  removeWidget: () => void
  showInvisible: (siteKey: string) => Promise<string>
}

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
let scriptLoadPromise: Promise<void> | null = null
let invisibleChallengePromise: Promise<string> | null = null

function loadTurnstileScript(): Promise<void> {
  // Return existing promise if script is already loading/loaded
  if (scriptLoadPromise) {
    return scriptLoadPromise
  }

  // Check if script is already in DOM
  if (document.querySelector(`script[src="${TURNSTILE_SCRIPT_URL}"]`)) {
    scriptLoadPromise = Promise.resolve()
    return scriptLoadPromise
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = TURNSTILE_SCRIPT_URL
    script.async = true
    script.defer = true

    script.onload = () => resolve()
    script.onerror = () => {
      scriptLoadPromise = null
      reject(new Error('Failed to load Turnstile script'))
    }

    document.head.appendChild(script)
  })

  return scriptLoadPromise
}

export function useTurnstile(options: UseTurnstileOptions = {}): UseTurnstileReturn {
  const token = ref<string | null>(null)
  const isLoading = ref(true)
  const error = ref<string | null>(null)
  const widgetId = ref<string | null>(null)

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || ''
  const isBypass = import.meta.env.VITE_TURNSTILE_BYPASS === 'true'

  const renderWidget = async (element: HTMLElement): Promise<string | null> => {
    if (isBypass) {
      isLoading.value = false
      return null
    }

    if (!siteKey) {
      error.value = 'Turnstile site key not configured'
      isLoading.value = false
      return null
    }

    // Wait for script to load
    try {
      await loadTurnstileScript()
    } catch (_err) {
      error.value = 'Failed to load security verification'
      isLoading.value = false
      return null
    }

    // Check if turnstile is available after script load
    if (!window.turnstile) {
      error.value = 'Turnstile not loaded'
      isLoading.value = false
      return null
    }

    // Remove existing widget if any
    if (widgetId.value) {
      window.turnstile.remove(widgetId.value)
    }

    try {
      const id = window.turnstile.render(element, {
        sitekey: siteKey,
        theme: options.theme || 'auto',
        callback: (newToken: string) => {
          token.value = newToken
          error.value = null
          options.onSuccess?.(newToken)
        },
        'error-callback': () => {
          error.value = 'Security verification failed. Please try again.'
          options.onError?.(new Error('Turnstile error callback'))
        },
        'expired-callback': () => {
          token.value = null
          options.onExpired?.()
        },
      })

      widgetId.value = id
      isLoading.value = false
      return id
    } catch (_err) {
      error.value = 'Failed to render security widget'
      isLoading.value = false
      return null
    }
  }

  const resetWidget = (): void => {
    token.value = null
    error.value = null

    if (isBypass || !window.turnstile || !widgetId.value) {
      return
    }

    window.turnstile.reset(widgetId.value)
  }

  const removeWidget = (): void => {
    if (isBypass || !window.turnstile || !widgetId.value) {
      return
    }

    window.turnstile.remove(widgetId.value)
    widgetId.value = null
    token.value = null
  }

  const showInvisible = async (providedSiteKey: string): Promise<string> => {
    if (isBypass) {
      return 'bypass-token'
    }

    const resolvedSiteKey = providedSiteKey || siteKey
    if (!resolvedSiteKey) {
      throw new Error('Turnstile site key not configured')
    }

    if (invisibleChallengePromise) {
      return invisibleChallengePromise
    }

    const challengePromise = (async () => {
      const container = document.createElement('div')
      container.style.cssText = 'position: fixed; left: -10000px; top: 0; visibility: hidden; pointer-events: none;'
      document.body.appendChild(container)

      try {
        await loadTurnstileScript()

        if (!window.turnstile) {
          throw new Error('Turnstile not loaded')
        }

        const turnstileApi = window.turnstile

        return await new Promise<string>((resolve, reject) => {
          let invisibleWidgetId: string | null = null

          const cleanup = () => {
            if (invisibleWidgetId) {
              turnstileApi.remove(invisibleWidgetId)
            }
            container.remove()
          }

          invisibleWidgetId = turnstileApi.render(container, {
            sitekey: resolvedSiteKey,
            size: 'invisible',
            theme: options.theme || 'auto',
            callback: (challengeToken: string) => {
              cleanup()
              resolve(challengeToken)
            },
            'error-callback': () => {
              cleanup()
              reject(new Error('Turnstile verification failed'))
            },
          })

          if (!invisibleWidgetId) {
            cleanup()
            reject(new Error('Failed to render Turnstile widget'))
            return
          }

          void turnstileApi.execute(invisibleWidgetId)
        })
      } catch (err) {
        if (container.isConnected) {
          container.remove()
        }
        throw err
      }
    })()

    invisibleChallengePromise = challengePromise

    try {
      return await challengePromise
    } finally {
      if (invisibleChallengePromise === challengePromise) {
        invisibleChallengePromise = null
      }
    }
  }

  onMounted(() => {
    if (isBypass) {
      isLoading.value = false
    }
  })

  onUnmounted(() => {
    removeWidget()
  })

  return {
    token,
    isLoading,
    error,
    isBypass,
    renderWidget,
    resetWidget,
    removeWidget,
    showInvisible,
  }
}
